/**
 * Zendesk widget configuration per user type.
 * Keys are loaded from environment variables for security.
 *
 * Local dev: Configure in .env.local (gitignored)
 * CI/CD: Configure in AWS CodeBuild environment variables
 */

export type ZendeskUserType = 'propertyManager' | 'renter'

/**
 * Zendesk keys per user type, loaded from environment variables.
 */
export const ZENDESK_KEYS: Record<ZendeskUserType, string> = {
  propertyManager: process.env.REACT_APP_ZENDESK_KEY_PM || '',
  renter: process.env.REACT_APP_ZENDESK_KEY_RENTER || '',
}

/**
 * Determines user type based on session state.
 * PM users have currentAgency with agencyID.
 * Renter users are authenticated but without agency context.
 * Returns null for unauthenticated users.
 */
export function getUserType(
  currentAgency: { agencyID?: string } | null,
  currentUser: unknown | null,
): ZendeskUserType | null {
  // Not authenticated - no widget
  if (!currentUser) {
    return null
  }

  // Has agency context with valid agencyID = Property Manager
  if (currentAgency?.agencyID) {
    return 'propertyManager'
  }

  // Authenticated but no agency = Renter
  return 'renter'
}

/**
 * Gets the Zendesk key for a given user type.
 * Returns null for unauthenticated users or if key not configured.
 */
export function getZendeskKey(userType: ZendeskUserType | null): string | null {
  if (!userType) {
    return null
  }

  const key = ZENDESK_KEYS[userType]
  return key || null
}
